import React, { useState, useEffect } from "react";
import { useGooglePlacesAutocomplete } from "../hooks/useGooglePlacesAutocomplete";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import "../App.css";
import "./HomePage.css";
import HeroSlideshow from "../components/HeroSlideshow";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import ItemDetailModal from "../components/ItemDetailModal";
import PackageBuyPromptModal from "../components/PackageBuyPromptModal";
import ComingSoonPage from "../components/ComingSoonPage";
import HomepageInfoSections from "../components/HomepageInfoSections";
import { formatProfessionalType } from "../utils/formatters";
import {
  buildHomepageSectionData,
  getHomepageRuntimeFlags,
} from "../utils/homepageRanking";
import { getDirectShopCataloguePath } from "../utils/shopUrls";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
const FEATURED_MAX_DISTANCE_MILES = 50;

const FALLBACK_CITY_COORDS = {
  "cary,il": [-88.2384, 42.212],
  "crystal lake,il": [-88.3226, 42.2411],
  "fox river grove,il": [-88.2148, 42.2009],
  "glenview,il": [-87.8028, 42.0698],
  "barrington,il": [-88.1362, 42.1539],
  "north barrington,il": [-88.1435, 42.2281],
  "south barrington,il": [-88.1545, 42.0931],
};

const parseDistanceMiles = (item) => {
  if (typeof item?._distanceMiles === "number" && Number.isFinite(item._distanceMiles)) {
    return item._distanceMiles;
  }

  if (typeof item?.distanceLabel === "string") {
    const match = item.distanceLabel.match(/([\d.]+)/);
    if (match) {
      const parsed = Number(match[1]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return null;
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const distanceMiles = (lat1, lng1, lat2, lng2) => {
  const earthRadiusMiles = 3958.8;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  return 2 * earthRadiusMiles * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const fallbackDistanceFromCityState = (userCoords, city, state) => {
  if (!userCoords) return null;

  const cityNorm = (city || "").trim().toLowerCase();
  const stateNorm = (state || "").trim().toLowerCase();
  const fallbackCoords = FALLBACK_CITY_COORDS[`${cityNorm},${stateNorm}`];

  if (!fallbackCoords || fallbackCoords.length !== 2) return null;

  const [lng, lat] = fallbackCoords;
  return distanceMiles(userCoords.lat, userCoords.lng, lat, lng);
};

function HomePage() {
  const MOBILE_FEATURED_INITIAL_COUNT = 6;
  const [featuredShops, setFeaturedShops] = useState([]);
  const navigate = useNavigate();
  const [featuredFreelancers, setFeaturedFreelancers] = useState([]);
  const [showAllFeaturedShopsMobile, setShowAllFeaturedShopsMobile] =
    useState(false);
  const [showAllFeaturedFreelancersMobile, setShowAllFeaturedFreelancersMobile] =
    useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  // The package whose "Buy package" prompt is open, or null. A card used to send every visitor to
  // the global packages listing, because there was no per-package customer route to send them to.
  // Phase 15 built that route; this holds the package the visitor actually clicked.
  const [buyPromptPackage, setBuyPromptPackage] = useState(null);
  const [stats, setStats] = useState({
    shops: 0,
    appointments: 0,
    professionals: 0,
  });
  const [comingSoon, setComingSoon] = useState(null); // { countryName, countryCode, message }
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [itemModal, setItemModal] = useState({
    isOpen: false,
    itemType: "product",
    itemId: null,
  });
  const [categories, setCategories] = useState([]);
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [featuredShopsReady, setFeaturedShopsReady] = useState(false);
  const [featuredFreelancersReady, setFeaturedFreelancersReady] = useState(false);
  const [featuredProductsReady, setFeaturedProductsReady] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState({
    enabled: true,
    ready: false,
  });

  // Geolocation for Near Me section
  const {
    selectedCoords: userCoords,
    locationSource,
    geolocationStatus,
    inputValue: cityInputValue,
    suggestions: citySuggestions,
    handleInputChange: handleCityInputChange,
    selectSuggestion: selectCitySuggestion,
    showSuggestions: showCitySuggestions,
    setShowSuggestions: setShowCitySuggestions,
    clearLocation: clearCityLocation,
  } = useGooglePlacesAutocomplete();
  const [heroSearchSuggestions, setHeroSearchSuggestions] = useState([]);

  useEffect(() => {
    const query = (cityInputValue || "").trim();
    if (query.length < 2) {
      setHeroSearchSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/search/suggestions`, {
          params: { q: query, limit: 8 },
        });
        setHeroSearchSuggestions(response.data?.suggestions || []);
      } catch (error) {
        setHeroSearchSuggestions([]);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [cityInputValue]);

  const handleHeroUnifiedSuggestionSelect = (suggestion) => {
    const term = (suggestion?.text || cityInputValue || "").trim();
    if (!term) return;
    setShowCitySuggestions(false);
    if (suggestion?.targetUrl) {
      navigate(suggestion.targetUrl);
      return;
    }
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const handleHeroSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowCitySuggestions(false);
      return;
    }

    if (e.key === "Enter") {
      const searchValue = (cityInputValue || "").trim();
      if (!searchValue) return;

      // Keep city autocomplete behavior when city suggestions are visible.
      const hasVisibleCitySuggestions =
        showCitySuggestions && citySuggestions.length > 0;
      if (hasVisibleCitySuggestions) return;

      e.preventDefault();
      setShowCitySuggestions(false);
      navigate(`/search?q=${encodeURIComponent(searchValue)}`);
    }
  };

  const shouldApplyHomepageDistanceTiers = !!userCoords;
  const proximityLabel =
    locationSource === "browser" && userCoords
      ? userCoords.label && userCoords.label !== "Current location"
        ? userCoords.label
        : null
      : null;

  const [nearShops, setNearShops] = useState([]);
  const [nearFreelancers, setNearFreelancers] = useState([]);
  // Fetch Near Me shops and freelancers if enabled and geolocation available
  useEffect(() => {
    const flags = getHomepageRuntimeFlags(homepageSettings);
    if (!flags.nearMeEnabled || !userCoords) return;
    // Fetch near shops
    axios
      .get(
        `${API_BASE_URL}/api/shops/public?nearLat=${userCoords.lat}&nearLng=${userCoords.lng}&limit=12`,
      )
      .then((res) => setNearShops(res.data.shops || []))
      .catch(() => setNearShops([]));
    // Fetch near freelancers
    axios
      .get(
        `${API_BASE_URL}/api/staff/public/featured?nearLat=${userCoords.lat}&nearLng=${userCoords.lng}&limit=12`,
      )
      .then((res) => setNearFreelancers(res.data.professionals || []))
      .catch(() => setNearFreelancers([]));
  }, [homepageSettings, userCoords]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    fetchFeaturedShops();
    fetchFeaturedFreelancers();
    fetchFeaturedProducts();
    fetchFeaturedPackages();
    fetchStats();
    fetchCategories();
    fetchPublicHomepageSettings();
    fetchOnboardingMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCoords, locationSource]);

  const fetchPublicHomepageSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings/public`);
      setHomepageSettings(response.data?.settings?.homepageSettings || null);
    } catch (err) {
      setHomepageSettings(null);
    }
  };

  const fetchOnboardingMode = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/onboarding-mode/public`,
      );
      setOnboardingMode({
        enabled: response.data?.onboardingMode?.enabled !== false,
        ready: true,
      });
    } catch (err) {
      // Keep enabled=true on failure to preserve existing behavior.
      setOnboardingMode({ enabled: true, ready: true });
    }
  };

  const fetchFeaturedShops = async () => {
    setFeaturedShopsReady(false);
    try {
      const params = new URLSearchParams();
      params.append("limit", "24");
      params.append("featured", "true");
      if (shouldApplyHomepageDistanceTiers) {
        params.append("lat", userCoords.lat);
        params.append("lng", userCoords.lng);
        params.append("radius", FEATURED_MAX_DISTANCE_MILES);

        if (locationSource === "manual") {
          const parts = userCoords.label?.split(",").map((s) => s.trim());
          const searchCity = parts?.[0];
          const stateFromLabel = parts?.[1]?.split(" ")[0];
          if (searchCity) params.append("searchCity", searchCity);
          if (stateFromLabel) params.append("state", stateFromLabel);
        }
      }

      const response = await axios.get(`${API_BASE_URL}/api/shops/public?${params.toString()}`);

      // Check if user is from a country where OGESTYLE is not yet available
      if (response.data.comingSoon) {
        setComingSoon({
          countryName: response.data.countryName,
          countryCode: response.data.userCountry,
          message: response.data.message,
        });
        return; // Don't show shops section
      }

      setFeaturedShops(response.data.shops || []);
    } catch (err) {
      // Silently fail - don't block page load
    } finally {
      setFeaturedShopsReady(true);
    }
  };

  const fetchFeaturedFreelancers = async () => {
    setFeaturedFreelancersReady(false);
    try {
      if (shouldApplyHomepageDistanceTiers) {
        const params = new URLSearchParams();
        params.append("limit", "24");
        params.append("lat", userCoords.lat);
        params.append("lng", userCoords.lng);
        params.append("radius", FEATURED_MAX_DISTANCE_MILES);

        if (locationSource === "manual") {
          const parts = userCoords.label?.split(",").map((s) => s.trim());
          const searchCity = parts?.[0];
          const stateFromLabel = parts?.[1]?.split(" ")[0];
          if (searchCity) params.append("searchCity", searchCity);
          if (stateFromLabel) params.append("state", stateFromLabel);
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/staff/freelancers?${params.toString()}`,
        );
        setFeaturedFreelancers(response.data.freelancers || []);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/staff/public/featured?limit=24`);
      setFeaturedFreelancers(response.data.professionals || []);
    } catch (err) {
      // Silently fail - don't block page load
    } finally {
      setFeaturedFreelancersReady(true);
    }
  };


  const rankedSections = buildHomepageSectionData({
    featuredShops,
    featuredFreelancers,
    nearShops,
    nearFreelancers,
    runtimeFlags: getHomepageRuntimeFlags(homepageSettings),
  });

  const visibleFeaturedShops =
    (rankedSections.featuredShops || []).length > 0
      ? rankedSections.featuredShops
      : featuredShops;
  const visibleFeaturedFreelancers =
    (rankedSections.featuredFreelancers || []).length > 0
      ? rankedSections.featuredFreelancers
      : featuredFreelancers;
  const visibleNearShops = rankedSections.nearShops;
  const visibleNearFreelancers = rankedSections.nearFreelancers;

  const hasHomepageDistanceAnchor = !!userCoords;

  const sortByDistanceAsc = (a, b) => {
    if (typeof a !== "number" && typeof b !== "number") return 0;
    if (typeof a !== "number") return 1;
    if (typeof b !== "number") return -1;
    return a - b;
  };

  const featuredShopsWithDistance = visibleFeaturedShops.map((shop) => ({
    ...shop,
    homeDistanceMiles: hasHomepageDistanceAnchor ? parseDistanceMiles(shop) : null,
  }));

  const featuredFreelancersWithDistance = visibleFeaturedFreelancers.map((professional) => ({
    ...professional,
    homeDistanceMiles: hasHomepageDistanceAnchor ? parseDistanceMiles(professional) : null,
  }));

  const nearbyFeaturedShops = featuredShopsWithDistance
    .filter(
      (shop) =>
        typeof shop.homeDistanceMiles === "number" &&
        shop.homeDistanceMiles <= FEATURED_MAX_DISTANCE_MILES,
    )
    .sort((a, b) => sortByDistanceAsc(a.homeDistanceMiles, b.homeDistanceMiles));

  const nearbyFeaturedFreelancers = featuredFreelancersWithDistance
    .filter(
      (professional) =>
        typeof professional.homeDistanceMiles === "number" &&
        professional.homeDistanceMiles <= FEATURED_MAX_DISTANCE_MILES,
    )
    .sort((a, b) =>
      sortByDistanceAsc(a.homeDistanceMiles, b.homeDistanceMiles),
    );

  const visibleFeaturedShopsForDisplay = shouldApplyHomepageDistanceTiers
    ? nearbyFeaturedShops
    : featuredShopsWithDistance;

  const visibleFeaturedFreelancersForDisplay = shouldApplyHomepageDistanceTiers
    ? nearbyFeaturedFreelancers
    : featuredFreelancersWithDistance;

  const canShowGeoEmptyState =
    shouldApplyHomepageDistanceTiers && geolocationStatus !== "requesting";

  const noCoverageShops =
    canShowGeoEmptyState && featuredShopsReady && nearbyFeaturedShops.length === 0;

  const noCoverageFreelancers =
    canShowGeoEmptyState &&
    featuredFreelancersReady &&
    nearbyFeaturedFreelancers.length === 0;
  // Helper: should show Near Me section
  const showNearMe = () => {
    const flags = getHomepageRuntimeFlags(homepageSettings);
    return flags.nearMeEnabled && userCoords && (visibleNearShops.length > 0 || visibleNearFreelancers.length > 0);
  };
      {/* Near Me Section */}
      {showNearMe() && (
        <div style={{ padding: isMobile ? "2px 16px" : "2px 48px", background: "#f8fafc", marginBottom: 24 }}>
          <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
            <h2 style={{ fontWeight: 700, color: "#1a659e", marginBottom: 8 }}>
              Near You: Beauty Businesses & Freelancers
            </h2>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {/* Near Shops */}
              {visibleNearShops.length > 0 && (
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontWeight: 600, color: "#111827", marginBottom: 6 }}>Shops Near You</h3>
                  <div className="homepage-horizontal-row" style={{ display: "flex", gap: 12, overflowX: "auto" }}>
                    {visibleNearShops.map((shop) => (
                      <div key={shop._id} style={{ minWidth: 140, background: "#fff", borderRadius: 10, padding: 10, marginBottom: 8 }}>
                        <div style={{ fontWeight: 700 }}>{shop.name}</div>
                        <div style={{ color: "#6b7280", fontSize: 12 }}>{shop.city}{shop.state ? `, ${shop.state}` : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Near Freelancers */}
              {visibleNearFreelancers.length > 0 && (
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontWeight: 600, color: "#111827", marginBottom: 6 }}>Freelancers Near You</h3>
                  <div className="homepage-horizontal-row" style={{ display: "flex", gap: 12, overflowX: "auto" }}>
                    {visibleNearFreelancers.map((pro) => (
                      <div key={pro._id} style={{ minWidth: 140, background: "#fff", borderRadius: 10, padding: 10, marginBottom: 8 }}>
                        <div style={{ fontWeight: 700 }}>{pro.displayName || pro.name}</div>
                        <div style={{ color: "#6b7280", fontSize: 12 }}>{pro.city}{pro.state ? `, ${pro.state}` : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

  const mobileVisibleRankedFeaturedShops =
    isMobile && !showAllFeaturedShopsMobile
      ? visibleFeaturedShopsForDisplay.slice(0, MOBILE_FEATURED_INITIAL_COUNT)
      : visibleFeaturedShopsForDisplay;

  const mobileVisibleRankedFeaturedFreelancers =
    isMobile && !showAllFeaturedFreelancersMobile
      ? visibleFeaturedFreelancersForDisplay.slice(0, MOBILE_FEATURED_INITIAL_COUNT)
      : visibleFeaturedFreelancersForDisplay;

  const fetchFeaturedProducts = async () => {
    setFeaturedProductsReady(false);
    try {
      const params = new URLSearchParams();
      params.append("limit", "24");

      if (shouldApplyHomepageDistanceTiers) {
        params.append("lat", userCoords.lat);
        params.append("lng", userCoords.lng);
        params.append("radius", FEATURED_MAX_DISTANCE_MILES);

        if (locationSource === "manual") {
          const parts = userCoords.label?.split(",").map((s) => s.trim());
          const searchCity = parts?.[0];
          const stateFromLabel = parts?.[1]?.split(" ")[0];
          if (searchCity) params.append("searchCity", searchCity);
          if (stateFromLabel) params.append("state", stateFromLabel);
        }
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/v2/products/featured?${params.toString()}`,
      );
      setFeaturedProducts(response.data.products || []);
    } catch (err) {
      // Silently fail - don't block page load
    } finally {
      setFeaturedProductsReady(true);
    }
  };

  const productsWithPickupMeta = featuredProducts
    .map((product) => {
      if (!shouldApplyHomepageDistanceTiers) {
        return {
          ...product,
          pickupDistanceMiles: null,
          pickupEligible: true,
        };
      }

      const normalizedDistanceMiles =
        parseDistanceMiles(product) ??
        fallbackDistanceFromCityState(
          userCoords,
          product.displayCity || product.city,
          product.displayState || product.state,
        );

      if (typeof normalizedDistanceMiles !== "number") {
        return {
          ...product,
          pickupDistanceMiles: null,
          pickupEligible: false,
        };
      }

      return {
        ...product,
        pickupDistanceMiles: normalizedDistanceMiles,
        pickupEligible: normalizedDistanceMiles <= FEATURED_MAX_DISTANCE_MILES,
      };
    });

  const nearbyPickupProducts = productsWithPickupMeta
    .filter(
      (product) =>
        typeof product.pickupDistanceMiles === "number" &&
        product.pickupDistanceMiles <= FEATURED_MAX_DISTANCE_MILES,
    )
    .sort((a, b) =>
      sortByDistanceAsc(a.pickupDistanceMiles, b.pickupDistanceMiles),
    );

  const showingNoCityCoverage =
    canShowGeoEmptyState &&
    featuredProductsReady &&
    nearbyPickupProducts.length === 0;

  const visibleFeaturedProducts = (
    shouldApplyHomepageDistanceTiers
      ? nearbyPickupProducts
      : productsWithPickupMeta
  ).slice(0, 16);

  const hasHomepageContent =
    !onboardingMode.ready || !onboardingMode.enabled
      ? true
      : visibleFeaturedShopsForDisplay.length > 0 ||
        visibleFeaturedFreelancersForDisplay.length > 0 ||
        visibleFeaturedProducts.length > 0 ||
        featuredPackages.length > 0;

  const shouldShowOnboardingCard = onboardingMode.ready && onboardingMode.enabled
    ? !hasHomepageContent
    : false;

  const fetchFeaturedPackages = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/packages/featured?limit=6`,
      );
      setFeaturedPackages(response.data.packages || []);
    } catch (err) {
      // Silently fail - don't block page load
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/shops/stats`);
      setStats(
        response.data || { shops: 0, appointments: 0, professionals: 0 },
      );
    } catch (err) {
      // Silently fail - don't block page load
    }
  };

  const fetchCategories = async () => {
    const normalizeType = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/shops/business-types/live`,
      );
      const liveCategories = (response.data.categories || []).slice(0, 13);
      if (liveCategories.length > 0) {
        setCategories(liveCategories);
        return;
      }

      throw new Error("live_categories_empty");
    } catch (err) {
      try {
        // Compatibility fallback for environments that do not yet expose /business-types/live.
        const [publicCategoriesRes, publicShopsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/shops/business-types/public`),
          axios.get(`${API_BASE_URL}/api/shops/public?limit=200`),
        ]);

        const publicCategories = publicCategoriesRes.data?.categories || [];
        const publicShops = publicShopsRes.data?.shops || [];

        const liveShopTypes = new Set();
        publicShops.forEach((shop) => {
          const types = Array.isArray(shop?.type) ? shop.type : [];
          types.forEach((typeValue) => {
            const normalized = normalizeType(typeValue);
            if (normalized) liveShopTypes.add(normalized);
          });

          const businessType = normalizeType(shop?.businessType);
          if (businessType) liveShopTypes.add(businessType);
        });

        const filteredLiveCategories = publicCategories
          .filter((category) => liveShopTypes.has(normalizeType(category.value)))
          .slice(0, 13);

        setCategories(filteredLiveCategories);
      } catch (fallbackErr) {
        // Keep empty on total failure to avoid showing non-live categories.
        setCategories([]);
      }
    }
  };

  const homepageCategoryCards = categories.slice(0, 13);

  const isGuestHomepage =
    typeof window !== "undefined" &&
    !window.localStorage.getItem("ogestyle_token");

  return (
    <>
      <CustomerHeader />
      <Helmet>
        <title>OGESTYLE - Book Beauty & Wellness Services Online</title>
        <meta
          name="description"
          content="Discover and book top-rated salons, spas, barbers, and independent beauty professionals. Online booking, secure payments, and trusted reviews."
        />
        <meta
          name="keywords"
          content="beauty salon booking, spa appointments, hair salon, barber shop, nail salon, beauty services, wellness booking"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ogestyle.com/" />
        <meta
          property="og:title"
          content="OGESTYLE - Book Beauty & Wellness Services Online"
        />
        <meta
          property="og:description"
          content="Discover and book top-rated salons, spas, barbers, and independent beauty professionals."
        />
        <meta property="og:image" content="https://www.ogestyle.com/og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.ogestyle.com/" />
        <meta
          property="twitter:title"
          content="OGESTYLE - Book Beauty & Wellness Services Online"
        />
        <meta
          property="twitter:description"
          content="Discover and book top-rated salons, spas, barbers, and independent beauty professionals."
        />
        <meta
          property="twitter:image"
          content="https://www.ogestyle.com/og-image.jpg"
        />

        <link rel="canonical" href="https://www.ogestyle.com/" />

        {/* Hide scrollbar for horizontal scroll */}
        <style>{`
          html, body { overflow-x: hidden; }
          div[style*="overflow-x: auto"]::-webkit-scrollbar,
          div[style*="overflowX: auto"]::-webkit-scrollbar {
            display: none;
          }
          div[style*="overflow-x: auto"],
          div[style*="overflowX: auto"] {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}</style>
      </Helmet>

      {/* Guest intro band — premium landing-style opening above the hero */}
      {isGuestHomepage && (
        <section className="hp-intro">
          <div className="hp-intro-inner">
            <p className="hp-eyebrow">Beauty booking, made simpler</p>
            <h1 className="hp-display">Book &amp; buy in one calm step</h1>
            <p className="hp-sub">
              Discover salons, studios and independent pros near you. Book a
              service in real time, shop the products they sell &mdash; or run
              your own beauty business.
            </p>

            <div className="hp-ctas">
              <Link to="/shops" className="hp-btn-primary">
                Browse &amp; book
              </Link>
              <Link to="/how-it-works" className="hp-link">
                How it works &rarr;
              </Link>
            </div>

            <div className="hp-audience">
              <span className="hp-audience-label">
                Built for everyone in beauty
              </span>
              <Link to="/shops" className="hp-pill">
                Clients &mdash; book or buy
              </Link>
              <Link to="/for-professionals" className="hp-pill">
                Professionals &mdash; join
              </Link>
              <Link to="/for-businesses" className="hp-pill">
                Business owners &mdash; grow
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Hero Slideshow with City Selector overlay */}
      <div
        style={{
          position: "relative",
          marginTop: isGuestHomepage
            ? "0px"
            : "calc(var(--demo-banner-height, 0px) + 50px)",
        }}
      >
        <HeroSlideshow />

        {/* Hero overlay - search pill (always); categories desktop-only */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: isMobile ? "16px" : "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isMobile ? "10px" : "12px",
            pointerEvents: "none",
            zIndex: 5,
            padding: isMobile ? "0 8px" : "0 24px",
          }}
        >
          <div
            style={{
              pointerEvents: "auto",
              background: "transparent",
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexDirection: isMobile ? "column" : "row",
              maxWidth: isMobile ? "92%" : "420px",
              width: isMobile ? "92%" : "auto",
            }}
          >
            <div style={{ position: "relative", width: isMobile ? "100%" : "320px" }}>
              <input
                type="text"
                value={cityInputValue}
                onChange={(e) => handleCityInputChange(e.target.value)}
                onFocus={() => setShowCitySuggestions(true)}
                placeholder="Search by business or city"
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid rgba(255,255,255,0.6)",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  outline: "none",
                  background: "rgba(255,255,255,0.85)",
                  backdropFilter: "blur(6px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                  transition: "border-color 0.2s",
                }}
                onKeyDown={(e) => {
                  handleHeroSearchKeyDown(e);
                }}
              />
              {cityInputValue && (
                <button
                  onClick={clearCityLocation}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    padding: "2px 6px",
                    color: "#6b7280",
                  }}
                  title="Clear selection"
                >
                  ✕
                </button>
              )}

              {/* City Suggestions Dropdown */}
              {showCitySuggestions &&
                (citySuggestions.length > 0 || heroSearchSuggestions.length > 0) && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    marginBottom: "4px",
                    background: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    maxHeight: "260px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {citySuggestions.map((suggestion, idx) => (
                    <div
                      key={`city-${idx}`}
                      onClick={() => {
                        selectCitySuggestion(suggestion);
                        setShowCitySuggestions(false);
                      }}
                      style={{
                        padding: "10px 14px",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: "#111827",
                        borderBottom: "1px solid #f3f4f6",
                        transition: "background-color 0.15s",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f9fafb")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <span>{suggestion.description}</span>
                      <span
                        style={{
                          fontSize: "10px",
                          background: "#eff6ff",
                          color: "#2d6da3",
                          padding: "2px 6px",
                          borderRadius: "10px",
                        }}
                      >
                        city
                      </span>
                    </div>
                  ))}

                  {heroSearchSuggestions.map((suggestion, idx) => (
                    <div
                      key={`search-${idx}`}
                      onClick={() => handleHeroUnifiedSuggestionSelect(suggestion)}
                      style={{
                        padding: "10px 14px",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: "#111827",
                        borderBottom:
                          idx < heroSearchSuggestions.length - 1
                            ? "1px solid #f3f4f6"
                            : "none",
                        transition: "background-color 0.15s",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f9fafb")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <span>
                        {suggestion.location
                          ? `${suggestion.text}, ${suggestion.location}`
                          : suggestion.text}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          background: "#ecfeff",
                          color: "#0e7490",
                          padding: "2px 6px",
                          borderRadius: "10px",
                          textTransform: "lowercase",
                        }}
                      >
                        {suggestion.type || "result"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!userCoords && geolocationStatus === "denied" && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#ffffff",
                  fontWeight: "500",
                  textShadow: "0 1px 2px rgba(0,0,0,0.45)",
                  whiteSpace: "nowrap",
                }}
              >
                Use search to set a city
              </div>
            )}

          </div>

          {/* Browse Services by Category - inside hero, under search (desktop only) */}
          {!isMobile && homepageCategoryCards.length > 0 && (
          <div
            style={{
              pointerEvents: "auto",
              background: "transparent",
              padding: 0,
              width: "100%",
              maxWidth: isMobile ? "100%" : "1680px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(homepageCategoryCards.length + 1, 14)}, minmax(106px, 112px))`,
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {homepageCategoryCards.map((category, idx) => {
                const colors = [
                  "#f59e0b",
                  "#3b82f6",
                  "#ec4899",
                  "#8b5cf6",
                  "#ef4444",
                  "#10b981",
                  "#f97316",
                  "#a855f7",
                  "#06b6d4",
                  "#84cc16",
                  "#fb923c",
                ];
                const color = colors[idx % colors.length];
                return (
                  <Link
                    key={idx}
                    to={`/shops?businessType=${category.value}`}
                    style={{
                      background: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "5px 6px 3px",
                      minHeight: "46px",
                      textAlign: "center",
                      textDecoration: "none",
                      transition: "all 0.3s",
                      cursor: "pointer",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 12px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 1px 3px rgba(0,0,0,0.1)";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "22px",
                        height: "24px",
                        marginBottom: "0px",
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {category.icon}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#111827",
                        lineHeight: 1.1,
                        minHeight: "24px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        marginTop: "2px",
                        overflow: "hidden",
                        textAlign: "center",
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {category.label}
                    </div>
                  </Link>
                );
              })}
              <Link
                to="/shops"
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "5px 6px 3px",
                  minHeight: "46px",
                  textAlign: "center",
                  textDecoration: "none",
                  transition: "all 0.3s",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#1a659e";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 12px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 1px 3px rgba(0,0,0,0.1)";
                }}
              >
                <div
                  style={{
                    fontSize: "22px",
                    height: "24px",
                    marginBottom: "0px",
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ➕
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#111827",
                    lineHeight: 1,
                    minHeight: "12px",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    marginTop: "2px",
                  }}
                >
                  More
                </div>
              </Link>
            </div>
          </div>
          )}
        </div>
      </div>

      {proximityLabel && (
        <div
          style={{
            padding: isMobile ? "14px 16px 0" : "16px 24px 0",
            background: "#fff",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              lineHeight: 1.4,
              color: "#0f172a",
            }}
          >
            {`Showing results near ${proximityLabel}`}
          </div>
        </div>
      )}

      {/* Country Availability Check */}
      {comingSoon ? (
        <>
          <ComingSoonPage
            countryName={comingSoon.countryName}
            countryCode={comingSoon.countryCode}
            source="homepage"
          />
          <CustomerFooter />
        </>
      ) : (
        <>

          {shouldShowOnboardingCard && (
            <div
              style={{
                background:
                  "linear-gradient(180deg, #f8fbff 0%, #ffffff 70%)",
                padding: isMobile ? "26px 16px 34px" : "42px 24px 54px",
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <div
                style={{
                  maxWidth: "1080px",
                  margin: "0 auto",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    background: "#e0ecff",
                    color: "#1e3a8a",
                    fontSize: "12px",
                    fontWeight: "700",
                    letterSpacing: "0.02em",
                  }}
                >
                  NOW ONBOARDING
                </div>
                <h2
                  style={{
                    margin: "12px 0 0",
                    color: "#0f172a",
                    fontSize: isMobile ? "22px" : "34px",
                    lineHeight: 1.15,
                    fontWeight: "800",
                  }}
                >
                  We are onboarding listings in your area
                </h2>
                <p
                  style={{
                    margin: "12px auto 0",
                    color: "#475569",
                    fontSize: isMobile ? "14px" : "16px",
                    lineHeight: 1.6,
                    maxWidth: "760px",
                  }}
                >
                  Claim your place early on OgeStyle. Whether you run a salon,
                  operate as an independent freelancer, or provide specialized
                  professional services, join now and be visible as we roll out
                  in more cities.
                </p>

                <div
                  style={{
                    marginTop: isMobile ? "18px" : "24px",
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(3, minmax(0, 1fr))",
                    gap: "12px",
                    textAlign: "left",
                  }}
                >
                  <Link
                    to="/for-businesses"
                    style={{
                      textDecoration: "none",
                      padding: "16px",
                      borderRadius: "14px",
                      border: "1px solid #dbeafe",
                      background: "#ffffff",
                      boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                      color: "#0f172a",
                      display: "block",
                    }}
                  >
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#1d4f80" }}>
                      FOR BUSINESS OWNERS
                    </div>
                    <div style={{ marginTop: "6px", fontSize: "15px", fontWeight: "700" }}>
                      Launch your business listing
                    </div>
                    <div style={{ marginTop: "4px", fontSize: "13px", color: "#475569" }}>
                      Set up your brand, locations, and services.
                    </div>
                  </Link>

                  <Link
                    to="/freelancer"
                    style={{
                      textDecoration: "none",
                      padding: "16px",
                      borderRadius: "14px",
                      border: "1px solid #dbeafe",
                      background: "#ffffff",
                      boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                      color: "#0f172a",
                      display: "block",
                    }}
                  >
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#1d4f80" }}>
                      FOR FREELANCERS
                    </div>
                    <div style={{ marginTop: "6px", fontSize: "15px", fontWeight: "700" }}>
                      Start as an independent provider
                    </div>
                    <div style={{ marginTop: "4px", fontSize: "13px", color: "#475569" }}>
                      Offer services and products with flexible schedule.
                    </div>
                  </Link>

                  <Link
                    to="/for-professionals"
                    style={{
                      textDecoration: "none",
                      padding: "16px",
                      borderRadius: "14px",
                      border: "1px solid #dbeafe",
                      background: "#ffffff",
                      boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
                      color: "#0f172a",
                      display: "block",
                    }}
                  >
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#1d4f80" }}>
                      FOR PROFESSIONALS
                    </div>
                    <div style={{ marginTop: "6px", fontSize: "15px", fontWeight: "700" }}>
                      Join the professional network
                    </div>
                    <div style={{ marginTop: "4px", fontSize: "13px", color: "#475569" }}>
                      Connect with clients seeking trusted experts.
                    </div>
                  </Link>
                </div>

                <div style={{ marginTop: "18px" }}>
                  <Link
                    to="/shops"
                    style={{
                      display: "inline-block",
                      background: "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                      color: "#fff",
                      textDecoration: "none",
                      padding: "11px 20px",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    Browse all businesses
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Featured Salons */}
          {visibleFeaturedShopsForDisplay.length > 0 && (
            <div
              style={{
                /*
                  SAME SEATING AS THE PACKAGES SECTION (user, 2026-10-23).

                  This used to be `32px 0px 8px` on desktop, i.e. NO side padding, so on an iPad
                  (which is not `isMobile`) the heading and the cards ran to the bezel while the
                  packages section sat comfortably inset. 48px matches the packages section exactly.
                */
                padding: isMobile ? "22px 16px 6px" : "32px 48px 8px",
                background: "#fff",
                overflow: "visible",
                height: "auto",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  maxWidth: "1400px",
                  margin: "0 auto",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header with View All button */}
                <div
                  className="homepage-horizontal-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: isMobile ? "6px" : "10px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {isMobile ? (
                      <h3
                        className="homepage-section-title"
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          margin: 0,
                          color: "#111827",
                          lineHeight: "1.15",
                        }}
                      >
                        Top-rated salons and beauty professionals in your area
                      </h3>
                    ) : (
                      <h2
                        className="homepage-section-title"
                        style={{
                          fontWeight: "700",
                          margin: 0,
                          color: "#111827",
                          lineHeight: "1.2",
                        }}
                      >
                        Top-rated salons and beauty professionals in your area
                      </h2>
                    )}
                  </div>
                  <Link
                    to="/shops"
                    style={{
                      background:
                        "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                      color: "white",
                      padding: isMobile ? "7px 12px" : "10px 20px",
                      borderRadius: isMobile ? "7px" : "8px",
                      textDecoration: "none",
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(26, 101, 158, 0.3)",
                      flexShrink: 0,
                      marginLeft: isMobile ? "10px" : "12px",
                    }}
                  >
                    View All
                  </Link>
                </div>

                {/* Single-row horizontal cards */}
                <div
                  className="homepage-horizontal-row"
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    overflowY: "hidden",
                    flexWrap: "nowrap",
                    gap: isMobile ? "12px" : "12px",
                    width: "100%",
                    padding: isMobile ? "4px 0 8px" : 0,
                    scrollSnapType: isMobile ? "x mandatory" : undefined,
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {mobileVisibleRankedFeaturedShops.length === 0 ? (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        minWidth: "100%",
                        width: "100%",
                        flex: "1 0 100%",
                        textAlign: "center",
                        padding: isMobile ? "28px 16px" : "40px 24px",
                        background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                        border: "1px dashed #bae6fd",
                        borderRadius: "14px",
                      }}
                    >
                      <div style={{ fontSize: isMobile ? "32px" : "40px", marginBottom: "8px" }}>✨</div>
                      <div style={{ fontSize: isMobile ? "14px" : "16px", fontWeight: 700, color: "#0f172a", marginBottom: "4px" }}>
                        Coming soon to your area
                      </div>
                      <div style={{ fontSize: isMobile ? "12px" : "13px", color: "#475569", lineHeight: 1.5, maxWidth: "520px", margin: "0 auto" }}>
                        We’re onboarding salons and spas near you. Be the first to know when they go live.
                      </div>
                      <Link
                        to="/shops"
                        style={{
                          display: "inline-block",
                          marginTop: "12px",
                          background: "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                          color: "white",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          textDecoration: "none",
                          fontSize: isMobile ? "12px" : "13px",
                          fontWeight: 600,
                        }}
                      >
                        Browse all businesses
                      </Link>
                    </div>
                  ) : mobileVisibleRankedFeaturedShops.map((shop) => {
                    // Generate navigation path using slugs only
                    const businessSlug = shop.businessId?.slug;
                    const locationSlug = shop.slug;
                    const shopPath = `/business/${businessSlug}/${locationSlug}`;

                    return (
                      <Link
                        key={shop._id}
                        to={shopPath}
                        style={{
                          minWidth: isMobile ? "160px" : "180px",
                          width: isMobile ? "160px" : "180px",
                          flexShrink: 0,
                          scrollSnapAlign: isMobile ? "start" : undefined,
                          background: "#fff",
                          borderRadius: "12px",
                          overflow: "visible",
                          textDecoration: "none",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                          transition: "all 0.3s",
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid #f3f4f6",
                          alignItems: "center",
                          margin: 0,
                          paddingBottom: isMobile ? "10px" : 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.10)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)";
                        }}
                      >
                        <div
                          style={{
                            height: isMobile ? "96px" : "110px",
                            width: isMobile ? "96px" : "110px",
                            borderRadius: "999px",
                            background: shop.businessId?.logo?.url
                              ? `url("${shop.businessId.logo.url}") center/cover no-repeat`
                              : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.10)",
                            color: "white",
                            fontSize: isMobile ? "28px" : "28px",
                            fontWeight: "700",
                            margin: "14px auto 8px auto",
                          }}
                        >
                          {!shop.businessId?.logo?.url && (shop.name?.[0] || "✨")}
                        </div>
                        <div
                          style={{
                            padding: "0 8px 8px 8px",
                            textAlign: "center",
                          }}
                        >
                          <h3
                            style={{
                              fontSize: isMobile ? "11px" : "14px",
                              fontWeight: "600",
                              color: "#111827",
                              marginTop: "0",
                              marginBottom: "2px",
                              lineHeight: "1.15",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {shop.name}
                          </h3>
                          <p
                            style={{
                              fontSize: isMobile ? "9px" : "12px",
                              color: "#6b7280",
                              marginTop: "0",
                              marginBottom: "2px",
                              lineHeight: "1.2",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {[shop.city, shop.state].filter(Boolean).join(", ") || "Location"}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "2px",
                              marginBottom: "0",
                              lineHeight: "0.95",
                            }}
                          >
                            <span
                              style={{
                                color: "#f59e0b",
                                fontSize: isMobile ? "9px" : "12px",
                              }}
                            >
                              ★★★★★
                            </span>
                            <span
                              style={{
                                fontSize: isMobile ? "9px" : "12px",
                                color: "#6b7280",
                              }}
                            >
                              (4.8)
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                {isMobile && visibleFeaturedShopsForDisplay.length > MOBILE_FEATURED_INITIAL_COUNT && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "10px",
                    }}
                  >
                    <button
                      onClick={() =>
                        setShowAllFeaturedShopsMobile(
                          !showAllFeaturedShopsMobile,
                        )
                      }
                      style={{
                        background: "white",
                        color: "#1a659e",
                        border: "1px solid #bfdbfe",
                        borderRadius: "999px",
                        padding: "6px 12px",
                        fontSize: "11px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      {showAllFeaturedShopsMobile ? "Show Less" : "More"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Featured Freelancers */}
          {visibleFeaturedFreelancersForDisplay.length > 0 && (
            <div
              style={{
                padding: isMobile ? "6px 16px" : "8px 48px",
                background: "#fff",
                overflow: "visible",
                height: "auto",
                boxSizing: "border-box",
                              }}
            >
              <div
                style={{
                  maxWidth: "1400px",
                  margin: "0 auto",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header with View All button */}
                <div
                  className="homepage-horizontal-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: isMobile ? "6px" : "10px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {isMobile ? (
                      <h3
                        className="homepage-section-title"
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          margin: 0,
                          color: "#111827",
                          lineHeight: "1.15",
                        }}
                      >
                        Talented freelance beauty professionals offering personalized services
                      </h3>
                    ) : (
                      <h2
                        className="homepage-section-title"
                        style={{
                          fontWeight: "700",
                          margin: 0,
                          color: "#111827",
                          lineHeight: "1.2",
                        }}
                      >
                        Talented freelance beauty professionals offering personalized services
                      </h2>
                    )}
                  </div>
                  <Link
                    to="/freelancers"
                    style={{
                      background:
                        "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                      color: "white",
                      padding: isMobile ? "7px 12px" : "10px 20px",
                      borderRadius: isMobile ? "7px" : "8px",
                      textDecoration: "none",
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(26, 101, 158, 0.3)",
                      flexShrink: 0,
                      marginLeft: isMobile ? "10px" : "12px",
                    }}
                  >
                    View All
                  </Link>
                </div>

                {/* Single-row horizontal cards */}
                <div
                  className="homepage-horizontal-row"
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    overflowY: "hidden",
                    flexWrap: "nowrap",
                    gap: isMobile ? "12px" : "12px",
                    width: "100%",
                    padding: isMobile ? "4px 0 8px" : 0,
                    scrollSnapType: isMobile ? "x mandatory" : undefined,
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {mobileVisibleRankedFeaturedFreelancers.length === 0 ? (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        minWidth: "100%",
                        width: "100%",
                        flex: "1 0 100%",
                        textAlign: "center",
                        padding: isMobile ? "28px 16px" : "40px 24px",
                        background: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)",
                        border: "1px dashed #f0abfc",
                        borderRadius: "14px",
                      }}
                    >
                      <div style={{ fontSize: isMobile ? "32px" : "40px", marginBottom: "8px" }}>💇‍♀️</div>
                      <div style={{ fontSize: isMobile ? "14px" : "16px", fontWeight: 700, color: "#0f172a", marginBottom: "4px" }}>
                        No independent pros in your area yet
                      </div>
                      <div style={{ fontSize: isMobile ? "12px" : "13px", color: "#475569", lineHeight: 1.5, maxWidth: "520px", margin: "0 auto" }}>
                        Talented freelancers are joining every week. Check back soon or explore pros offering travel services.
                      </div>
                      <Link
                        to="/freelancers"
                        style={{
                          display: "inline-block",
                          marginTop: "12px",
                          background: "linear-gradient(135deg, #a21caf 0%, #d946ef 100%)",
                          color: "white",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          textDecoration: "none",
                          fontSize: isMobile ? "12px" : "13px",
                          fontWeight: 600,
                        }}
                      >
                        Explore all professionals
                      </Link>
                    </div>
                  ) : mobileVisibleRankedFeaturedFreelancers.map((professional) => (
                    <Link
                      key={professional._id}
                      to={
                        professional.role === "R7"
                          ? professional.slug
                            ? `/freelancers/${professional.slug}`
                            : `/freelancers/${professional._id}`
                          :
                        professional.slug
                          ? `/freelancer/${professional.slug}`
                          : `/freelancer/${professional._id}`
                      }
                      style={{
                        minWidth: isMobile ? "160px" : "180px",
                        width: isMobile ? "160px" : "180px",
                        flexShrink: 0,
                        scrollSnapAlign: isMobile ? "start" : undefined,
                        background: "#fff",
                        borderRadius: "12px",
                        overflow: "visible",
                        textDecoration: "none",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                        transition: "all 0.3s",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #f3f4f6",
                        alignItems: "center",
                        margin: 0,
                        paddingBottom: isMobile ? "10px" : 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.10)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)";
                      }}
                    >
                      <div
                        style={{
                          width: isMobile ? "96px" : "110px",
                          height: isMobile ? "96px" : "110px",
                          borderRadius: "999px",
                          background: professional.brandImage
                            ? `url("${professional.brandImage}") center/cover no-repeat`
                            : `linear-gradient(135deg, ${["#2d6da3", "#f093fb", "#4facfe", "#43e97b", "#fa709a"][professional._id.charCodeAt(0) % 5]} 0%, ${["#4a90c6", "#f5576c", "#00f2fe", "#38f9d7", "#fee140"][professional._id.charCodeAt(0) % 5]} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: isMobile ? "28px" : "28px",
                          fontWeight: "700",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.10)",
                          margin: "14px auto 8px auto",
                        }}
                      >
                        {!professional.brandImage &&
                          (professional.firstName?.[0] || "?")}
                      </div>
                      <div
                        style={{
                          padding: "0 8px 8px 8px",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: isMobile ? "11px" : "14px",
                            fontWeight: "600",
                            color: "#111827",
                            marginTop: "0",
                            marginBottom: "2px",
                            lineHeight: "1.15",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {professional.businessName ||
                            `${professional.firstName} ${professional.lastName}`}
                        </h3>
                        <p
                          style={{
                            fontSize: isMobile ? "9px" : "11px",
                            color: "#1a659e",
                            marginTop: "0",
                            marginBottom: "2px",
                            fontWeight: "500",
                            lineHeight: "1.2",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatProfessionalType(
                            professional.professionalType ||
                              professional.primaryProfessionalType ||
                              professional.professionalTypes?.[0] ||
                              "",
                          )}
                        </p>
                        <p
                          style={{
                            fontSize: isMobile ? "9px" : "12px",
                            color: "#6b7280",
                            marginTop: "0",
                            marginBottom: "2px",
                            lineHeight: "1.2",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {[professional.city, professional.state]
                            .filter(Boolean)
                            .join(", ") || "Location"}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "2px",
                            lineHeight: "0.95",
                          }}
                        >
                          <span
                            style={{
                              color: "#f59e0b",
                              fontSize: isMobile ? "9px" : "12px",
                            }}
                          >
                            ★★★★★
                          </span>
                          <span
                            style={{
                              fontSize: isMobile ? "9px" : "12px",
                              color: "#6b7280",
                            }}
                          >
                            (5.0)
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                {isMobile &&
                  visibleFeaturedFreelancersForDisplay.length > MOBILE_FEATURED_INITIAL_COUNT && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        onClick={() =>
                          setShowAllFeaturedFreelancersMobile(
                            !showAllFeaturedFreelancersMobile,
                          )
                        }
                        style={{
                          background: "white",
                          color: "#1a659e",
                          border: "1px solid #bfdbfe",
                          borderRadius: "999px",
                          padding: "6px 12px",
                          fontSize: "11px",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        {showAllFeaturedFreelancersMobile ? "Show Less" : "More"}
                      </button>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Featured Products */}
          {visibleFeaturedProducts.length > 0 && (
            <div
              style={{
                padding: isMobile ? "6px 16px 20px" : "8px 48px 28px",
                background: "#fff",
                overflow: "visible",
                height: "auto",
                boxSizing: "border-box",
                              }}
            >
              <div
                style={{
                  maxWidth: "1400px",
                  margin: "0 auto",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header with View All button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: isMobile ? "6px" : "10px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {isMobile ? (
                      <h3
                        className="homepage-section-title"
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          margin: 0,
                          color: "#111827",
                          lineHeight: "1.15",
                        }}
                      >
                        Professional-grade beauty products recommended by top stylists
                      </h3>
                    ) : (
                      <h2
                        className="homepage-section-title"
                        style={{
                          fontWeight: "700",
                          margin: 0,
                          color: "#111827",
                          lineHeight: "1.2",
                        }}
                      >
                        Professional-grade beauty products recommended by top stylists
                      </h2>
                    )}
                  </div>
                  <Link
                    to="/products"
                    style={{
                      background:
                        "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                      color: "white",
                      padding: isMobile ? "7px 12px" : "10px 20px",
                      borderRadius: isMobile ? "7px" : "8px",
                      textDecoration: "none",
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(26, 101, 158, 0.3)",
                      flexShrink: 0,
                      marginLeft: isMobile ? "10px" : "12px",
                    }}
                  >
                    {isMobile ? "Shop All →" : "Shop All Products →"}
                  </Link>
                </div>

                {/* Single-row horizontal cards */}
                <div
                  className="homepage-horizontal-row"
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    overflowY: "hidden",
                    flexWrap: "nowrap",
                    gap: isMobile ? "12px" : "12px",
                    width: "100%",
                    padding: isMobile ? "4px 0 8px" : 0,
                    scrollSnapType: isMobile ? "x mandatory" : undefined,
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {visibleFeaturedProducts.length === 0 ? (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        minWidth: "100%",
                        width: "100%",
                        flex: "1 0 100%",
                        textAlign: "center",
                        padding: isMobile ? "28px 16px" : "40px 24px",
                        background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                        border: "1px dashed #6ee7b7",
                        borderRadius: "14px",
                      }}
                    >
                      <div style={{ fontSize: isMobile ? "32px" : "40px", marginBottom: "8px" }}>🛍️</div>
                      <div style={{ fontSize: isMobile ? "14px" : "16px", fontWeight: 700, color: "#0f172a", marginBottom: "4px" }}>
                        {showingNoCityCoverage
                          ? "No pickup-ready products in your city yet"
                          : "Featured products coming soon"}
                      </div>
                      <div style={{ fontSize: isMobile ? "12px" : "13px", color: "#475569", lineHeight: 1.5, maxWidth: "520px", margin: "0 auto" }}>
                        {showingNoCityCoverage
                          ? "We’re adding stockists in your area. In the meantime, browse our full catalog."
                          : "Our editors are curating fresh picks. Browse the full shop to see what’s available now."}
                      </div>
                      <Link
                        to="/products"
                        style={{
                          display: "inline-block",
                          marginTop: "12px",
                          background: "linear-gradient(135deg, #047857 0%, #10b981 100%)",
                          color: "white",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          textDecoration: "none",
                          fontSize: isMobile ? "12px" : "13px",
                          fontWeight: 600,
                        }}
                      >
                        Shop all products
                      </Link>
                    </div>
                  ) : (
                    visibleFeaturedProducts.map((product) => {
                      const businessName =
                        product.displayBrandName ||
                        product.displayBusinessName ||
                        product.businessName ||
                        "Shop";

                      const cityState =
                        (product.displayCity && product.displayState
                          ? `${product.displayCity}, ${product.displayState}`
                          : product.city && product.state
                            ? `${product.city}, ${product.state}`
                            : "Location");

                      return (
                        <div
                          key={product._id}
                          onClick={() =>
                            setItemModal({
                              isOpen: true,
                              itemType: "product",
                              itemId: product._id,
                            })
                          }
                          style={{
                            minWidth: isMobile ? "170px" : "190px",
                            width: isMobile ? "170px" : "190px",
                            flexShrink: 0,
                            scrollSnapAlign: isMobile ? "start" : undefined,
                            background: "#fff",
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "none",
                            border: "none",
                            transition: "all 0.3s",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                        <div
                          style={{
                            height: isMobile ? "150px" : "180px",
                            width: "100%",
                            background:
                              product.images && product.images.length > 0
                                ? `url("${product.images[0]}") center center / contain no-repeat, #fff`
                                : `linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            borderRadius: "12px 12px 0 0",
                          }}
                        >
                          {(!product.images || product.images.length === 0) && (
                            <div style={{ fontSize: "40px" }}>💄</div>
                          )}
                          {/* Featured Badge */}
                          {product.featured && (
                            <div
                              style={{
                                position: "absolute",
                                top: "8px",
                                left: "8px",
                                background: "#f59e0b",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "9px",
                                fontWeight: "700",
                              }}
                            >
                              FEATURED
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            padding: "2px 8px 8px 8px",
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            textAlign: "center",
                          }}
                        >
                          <h3
                            style={{
                              fontSize: isMobile ? "12px" : "14px",
                              fontWeight: "600",
                              color: "#111827",
                              marginBottom: "0px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {product.name || "Product"}
                          </h3>
                          <div style={{ marginTop: "auto" }}>
                            <div
                              style={{
                                fontSize: isMobile ? "12px" : "14px",
                                fontWeight: "700",
                                color: "#10b981",
                                marginBottom: "4px",
                              }}
                            >
                              $
                              {product.price
                                ? product.price.toFixed(2)
                                : "0.00"}
                            </div>
                            <div
                              style={{
                                fontSize: isMobile ? "10px" : "12px",
                                color: "#6b7280",
                                marginBottom: "6px",
                                lineHeight: "1.25",
                              }}
                            >
                              <div
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {businessName}
                              </div>
                              <div
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {cityState}
                              </div>
                            </div>
                            <button
                              type="button"
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                background: "#2d6da3",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                fontWeight: "600",
                                cursor: "pointer",
                                fontSize: isMobile ? "10px" : "12px",
                                transition: "background 0.2s",
                                textAlign: "center",
                                margin: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxSizing: "border-box",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "#1e5a8a")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "#2d6da3")
                              }
                            >
                              Quick View
                            </button>
                          </div>
                        </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Featured Packages Section */}
          {featuredPackages.length > 0 && (
            <div
              style={{
                /* Seated like the sections around it (user, 2026-10-23): 8px of top padding and a
                   28px tail, matching freelancers and products, instead of its own 2px rhythm. */
                padding: isMobile ? "8px 16px 28px" : "8px 48px 28px",
                background: "#f9fafb",
                overflow: "visible",
                height: "auto",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  maxWidth: "1400px",
                  margin: "0 auto",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header with View All button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: isMobile ? "8px" : "10px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {isMobile ? (
                      <h3
                        style={{
                          fontWeight: "700",
                          marginBottom: "2px",
                          color: "#111827",
                          lineHeight: "1.2",
                        }}
                      >
                        💸 Save Big with Package Deals
                      </h3>
                    ) : (
                      <h2
                        style={{
                          fontWeight: "700",
                          marginBottom: "2px",
                          color: "#111827",
                          lineHeight: "1.2",
                        }}
                      >
                        💸 Save Big with Package Deals
                      </h2>
                    )}
                    <p
                      style={{
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#6b7280",
                        lineHeight: "1.4",
                        margin: 0,
                      }}
                    >
                      Exclusive multi-service packages from top-rated salons.
                      Get more for less!
                    </p>
                  </div>
                  <Link
                    to="/packages"
                    style={{
                      background:
                        "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                      color: "white",
                      padding: isMobile ? "8px 16px" : "10px 20px",
                      borderRadius: "8px",
                      textDecoration: "none",
                      fontSize: isMobile ? "12px" : "13px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(26, 101, 158, 0.3)",
                      flexShrink: 0,
                      marginLeft: "12px",
                    }}
                  >
                    View All Packages →
                  </Link>
                </div>

                {/* Horizontal scrollable cards */}
                <div
                  className="homepage-horizontal-row"
                  style={{
                    display: "flex",
                    gap: "18px",
                    overflowX: "auto",
                    overflowY: "visible",
                    paddingBottom: "0px",
                    flex: 1,
                  }}
                >
                  {featuredPackages.map((pkg) => (
                    <div
                      key={pkg._id}
                      style={{
                        minWidth: "180px",
                        width: "180px",
                        // Was a hard `height: 200px`, which clipped the card: the name, shop,
                        // services box and the price/button block together exceed 200px, so the
                        // button and part of the price row were cut off. A minimum height keeps the
                        // row visually even while letting a longer card grow to fit its content.
                        minHeight: "200px",
                        background: "white",
                        borderRadius: "12px",
                        padding: "10px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        cursor: "pointer",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #e5e7eb",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow =
                          "0 12px 32px rgba(0,0,0,0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(0,0,0,0.08)";
                      }}
                      onClick={() => {
                        // Clicking a package means "I want THIS one", not "show me all packages".
                        // It opens a prompt that says what the package is and hands over to its own
                        // purchase page. `/packages` stays the target of "View All Packages" only.
                        setBuyPromptPackage(pkg);
                      }}
                    >
                      {/* Savings Badge */}
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background:
                            "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: "700",
                          boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)",
                        }}
                      >
                        {pkg.savingsPercentage || 0}% OFF
                      </div>

                      {/* Package Name */}
                      <h3
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "4px",
                          color: "#111827",
                          paddingRight: "60px",
                        }}
                      >
                        {pkg.name}
                      </h3>

                      {/* Shop Info */}
                      {pkg.shopId && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#6b7280",
                            marginBottom: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span>📍</span>
                          <span>{pkg.shopId.name}</span>
                        </div>
                      )}

                      {/* Services List */}
                      {pkg.services && pkg.services.length > 0 && (
                        <div
                          style={{
                            background: "#f0f9ff",
                            padding: "8px",
                            borderRadius: "6px",
                            marginBottom: "8px",
                            border: "1px solid #bfdbfe",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#1e40af",
                              fontWeight: "600",
                              marginBottom: "4px",
                            }}
                          >
                            Services Included:
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#374151",
                              lineHeight: "1.5",
                            }}
                          >
                            {pkg.services.slice(0, 2).map((service, idx) => (
                              <div key={idx} style={{ marginBottom: "2px" }}>
                                ✓{" "}
                                {service.serviceId?.name ||
                                  service.serviceName ||
                                  "Service"}
                              </div>
                            ))}
                            {pkg.services.length > 2 && (
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#6b7280",
                                  marginTop: "2px",
                                }}
                              >
                                +{pkg.services.length - 2} more service
                                {pkg.services.length - 2 > 1 ? "s" : ""}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Price and Button Container - pushed to bottom */}
                      <div style={{ marginTop: "auto" }}>
                        {/* Price Info */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "6px",
                          }}
                        >
                          <div>
                            <h2
                              style={{
                                fontWeight: "700",
                                color: "#1a659e",
                                margin: 0,
                              }}
                            >
                              ${pkg.packagePrice?.toFixed(2) || "0.00"}
                            </h2>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#9ca3af",
                                textDecoration: "line-through",
                              }}
                            >
                              ${pkg.regularPrice?.toFixed(2) || "0.00"}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#059669",
                            }}
                          >
                            Save ${pkg.savings?.toFixed(2) || "0.00"}
                          </div>
                        </div>

                        {/* CTA Button. It buys, so it says so — it previously read "View Packages →",
                            which described the old fallback (a jump to the listing) rather than an
                            action on this package. */}
                        <h3
                          style={{
                            width: "100%",
                            padding: "6px",
                            background:
                              "linear-gradient(135deg, #1a659e 0%, #004e89 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontWeight: "600",
                            cursor: "pointer",
                            textAlign: "center",
                            margin: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                          }}
                        >
                          Buy Package →
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* The step between clicking a package and paying for it. It renders nothing when no
              package is chosen, so mounting it unconditionally is safe. */}
          <PackageBuyPromptModal
            pkg={buyPromptPackage}
            onClose={() => setBuyPromptPackage(null)}
          />

          {/* Platform Stats Section - Commented out until we have good shop counts
      <div style={{
        padding: isMobile ? '40px 16px' : '60px 48px',
        background: '#f9fafb'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: isMobile ? '14px' : '14px',
            fontWeight: '700',
            marginBottom: isMobile ? '24px' : '40px',
            color: '#111827'
          }}>
            Trusted by Thousands of Happy Customers
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '24px' : '40px'
          }}>
            <div>
              <div style={{
                fontSize: isMobile ? '14px' : '14px',
                fontWeight: '700',
                color: '#1a659e',
                marginBottom: '8px'
              }}>
                {stats.shops.toLocaleString()}+
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Partner Businesses
              </div>
            </div>
            <div>
              <div style={{
                fontSize: isMobile ? '14px' : '14px',
                fontWeight: '700',
                color: '#1a659e',
                marginBottom: '8px'
              }}>
                {stats.appointments.toLocaleString()}+
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Appointments Booked
              </div>
            </div>
            <div>
              <div style={{
                fontSize: isMobile ? '14px' : '14px',
                fontWeight: '700',
                color: '#1a659e',
                marginBottom: '8px'
              }}>
                {stats.professionals.toLocaleString()}+
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Beauty Professionals
              </div>
            </div>
          </div>
        </div>
      </div>
      */}

          <HomepageInfoSections />

          <CustomerFooter />
        </>
      )}

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={itemModal.isOpen}
        onClose={() =>
          setItemModal({ isOpen: false, itemType: "product", itemId: null })
        }
        itemType={itemModal.itemType}
        itemId={itemModal.itemId}
      />
    </>
  );
}

export default HomePage;
